import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{title}</h2>
    <div className="space-y-2 text-neutral-600 dark:text-neutral-400 leading-relaxed">
      {children}
    </div>
  </section>
)

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-surface-50 dark:bg-dark-300 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Back link */}
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </Link>

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-500">Last updated: 22 March 2026</p>
        </div>

        <div className="bg-white dark:bg-dark-200 rounded-2xl border border-neutral-200 dark:border-neutral-700 shadow-sm p-8 space-y-8">

          <Section title="1. Who We Are">
            <p>
              WebNest ("we", "us", "our") is a web design and content management service operated by
              Marco Alcini. We provide website building, hosting, and management tools to small businesses.
            </p>
            <p>
              Contact us at:{' '}
              <a href="mailto:hello@marcoalcini.com" className="text-accent-600 dark:text-accent-400 hover:underline">
                hello@marcoalcini.com
              </a>
            </p>
          </Section>

          <Section title="2. What Data We Collect">
            <p>When you use WebNest, we collect:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong className="text-neutral-700 dark:text-neutral-300">Account data</strong> — your name, email address, and password (stored as an encrypted hash)</li>
              <li><strong className="text-neutral-700 dark:text-neutral-300">Company data</strong> — business name, contact details, and industry</li>
              <li><strong className="text-neutral-700 dark:text-neutral-300">Website content</strong> — text, images, and files you upload to build your website</li>
              <li><strong className="text-neutral-700 dark:text-neutral-300">Billing data</strong> — subscription plan and payment history. Card details are processed directly by Stripe and never stored on our servers</li>
              <li><strong className="text-neutral-700 dark:text-neutral-300">Usage data</strong> — login timestamps and activity within the platform</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Data">
            <p>We use your data to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Provide and manage your WebNest account and website</li>
              <li>Process subscription payments via Stripe</li>
              <li>Send service notifications and updates by email</li>
              <li>Respond to support requests</li>
              <li>Maintain the security and performance of the platform</li>
            </ul>
            <p>We do not sell your data to third parties. We do not use your data for advertising.</p>
          </Section>

          <Section title="4. Third-Party Services">
            <p>We use the following trusted third-party services to operate the platform:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden mt-2">
                <thead className="bg-neutral-50 dark:bg-dark-300">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-neutral-700 dark:text-neutral-300">Service</th>
                    <th className="text-left px-4 py-2 font-medium text-neutral-700 dark:text-neutral-300">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                  <tr>
                    <td className="px-4 py-2 font-medium text-neutral-700 dark:text-neutral-300">Microsoft Azure</td>
                    <td className="px-4 py-2">Cloud hosting and file storage</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium text-neutral-700 dark:text-neutral-300">Stripe</td>
                    <td className="px-4 py-2">Payment processing</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium text-neutral-700 dark:text-neutral-300">Resend</td>
                    <td className="px-4 py-2">Transactional email delivery</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Each provider is contractually bound to handle your data securely and in compliance with UK GDPR.
            </p>
          </Section>

          <Section title="5. Cookies">
            <p>
              WebNest uses only essential, functional browser storage necessary to keep you logged in and
              remember your preferences. We do not use tracking, analytics, or advertising cookies.
              No cookie consent is required for essential cookies under UK GDPR.
            </p>
          </Section>

          <Section title="6. Data Storage and Security">
            <p>
              Your data is stored on Microsoft Azure servers. We implement appropriate technical measures
              including encrypted passwords, secure HTTPS connections, and access controls to protect your data.
            </p>
          </Section>

          <Section title="7. Data Retention">
            <p>
              We retain your data for as long as your account is active. If you close your account, your
              personal data will be deleted within 30 days, except where we are required to retain it for
              legal or financial record-keeping purposes (e.g. billing records, up to 7 years under UK law).
            </p>
          </Section>

          <Section title="8. Your Rights Under UK GDPR">
            <p>You have the right to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong className="text-neutral-700 dark:text-neutral-300">Access</strong> the personal data we hold about you</li>
              <li><strong className="text-neutral-700 dark:text-neutral-300">Correct</strong> inaccurate data</li>
              <li><strong className="text-neutral-700 dark:text-neutral-300">Delete</strong> your data ("right to be forgotten")</li>
              <li><strong className="text-neutral-700 dark:text-neutral-300">Restrict</strong> or <strong className="text-neutral-700 dark:text-neutral-300">object</strong> to processing</li>
              <li><strong className="text-neutral-700 dark:text-neutral-300">Data portability</strong> — receive your data in a portable format</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:hello@marcoalcini.com" className="text-accent-600 dark:text-accent-400 hover:underline">
                hello@marcoalcini.com
              </a>
              . We will respond within 30 days.
            </p>
            <p>
              You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at{' '}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-accent-600 dark:text-accent-400 hover:underline">
                ico.org.uk
              </a>
              .
            </p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>
              We may update this policy from time to time. We will notify you by email of any significant
              changes. Continued use of the service after changes constitutes acceptance.
            </p>
          </Section>

        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-neutral-500 dark:text-neutral-500 space-x-4">
          <Link to="/terms" className="hover:text-accent-600 dark:hover:text-accent-400 transition-colors">
            Terms &amp; Conditions
          </Link>
          <span>·</span>
          <Link to="/login" className="hover:text-accent-600 dark:hover:text-accent-400 transition-colors">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
